/**
 * MembersTab - Table view of organization members with search and filters
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Search, MoreHorizontal, MapPin, X, ChevronLeft, ChevronRight, KeyRound, Download } from 'lucide-react';
import { media } from '../../styles/media';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { ResponsiveTableWrapper } from '../../components/ui';
import { WarningToast, useAutoDismissWarning } from '../../components/ui/WarningToast';
import { TempPasswordModal } from '../../components/ui/TempPasswordModal';
import userService, { Member } from '../../services/api/userService';
import memberService, { ActivateLoginCredential } from '../../services/api/memberService';
import { downloadCredentialsCsv } from '../shared/bulkUploadUtils';
import { useAuthStore } from '../../stores/authStore';
import { EditMemberModal } from './EditMemberModal';
import { DepartureWizardModal } from './DepartureWizardModal';
import { ReactivateMemberModal } from './ReactivateMemberModal';
import { BulkAssignLocationPanel } from './BulkAssignLocationPanel';

interface MembersTabProps {
  onMemberClick: (member: Member) => void;
}

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;
`;

const SearchWrapper = styled.div`
  flex: 1;
  min-width: 220px;
  max-width: 320px;
`;

const FilterSelect = styled.select`
  height: 44px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }
`;

const TableWrapper = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1020px;
`;

const TableHead = styled.thead`
  background: ${({ theme }) => theme.colors.backgrounds.gray};
`;

const TableHeader = styled.th`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  white-space: nowrap;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  cursor: pointer;

  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.page};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  vertical-align: middle;
`;

const NameCell = styled(TableCell)`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  white-space: nowrap;
`;

const EmailCell = styled(TableCell)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const GroupsCell = styled(TableCell)`
  max-width: 280px;
`;

const GroupChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const GroupChip = styled.span<{ $index: number }>`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  white-space: nowrap;
  ${({ $index, theme }) => {
    const palettes = [
      `background: ${theme.colors.status.info.bg}; color: ${theme.colors.status.info.text}; border: 1px solid ${theme.colors.status.info.border};`,
      `background: ${theme.colors.status.locked.bg}; color: ${theme.colors.status.locked.text}; border: 1px solid ${theme.colors.status.locked.border};`,
      `background: ${theme.colors.status.warning.bg}; color: ${theme.colors.status.warning.text}; border: 1px solid ${theme.colors.status.warning.border};`,
      `background: ${theme.colors.status.success.bg}; color: ${theme.colors.status.success.text}; border: 1px solid ${theme.colors.status.success.border};`,
    ];
    return palettes[$index % palettes.length];
  }}
`;

const StatusText = styled.span<{ $active: boolean }>`
  color: ${({ theme, $active }) =>
    $active ? theme.colors.status.success.text : theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const ActionsCell = styled(TableCell)`
  width: 40px;
  padding-right: ${({ theme }) => theme.spacing[3]};
`;

const CheckHeader = styled(TableHeader)`
  width: 40px;
  padding-right: 0;
`;

const CheckCell = styled(TableCell)`
  width: 40px;
  padding-right: 0;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.primary.base};
`;

const SelectionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.primary.base};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.primary.lightSky ?? theme.colors.backgrounds.light};
`;

const SelectionCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SelectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-wrap: wrap;
`;

const CredentialsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.status.warning.bg};
`;

const CredentialsText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.warning.text};
`;

const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const DropdownMenu = styled.div<{ $open: boolean }>`
  position: fixed;
  min-width: 160px;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  overflow: hidden;
`;

const DropdownItem = styled.button<{ $danger?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme, $danger }) =>
    $danger ? theme.colors.status.error.text : theme.colors.text.primary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.light};
  }
`;

const MenuWrapper = styled.div`
  position: relative;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};

  ${media.tablet} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const PaginationInfo = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing[12]};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const LoadingState = styled(EmptyState)``;

const NoneText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const RowMenuCell = ({
  member,
  onViewDetails,
  onEdit,
  onDepart,
  onReactivate,
  onActivateLogin,
  canEdit,
  canEditMember,
  canDepart,
  canReactivate,
}: {
  member: Member;
  onViewDetails: (m: Member) => void;
  onEdit: (m: Member) => void;
  onDepart: (m: Member) => void;
  onReactivate: (m: Member) => void;
  onActivateLogin: (m: Member) => void;
  canEdit: boolean;
  canEditMember: boolean;
  canDepart: boolean;
  canReactivate: boolean;
}) => {
  const isActive = member.status === 'active';
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top?: number; bottom?: number; right: number } | null>(
    null,
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Position the (portaled) menu with fixed coordinates derived from the trigger button,
  // opening upward when there isn't room below — so the last rows are never clipped by the
  // table wrapper's overflow.
  const computePosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const right = window.innerWidth - rect.right;
    const spaceBelow = window.innerHeight - rect.bottom;
    const ESTIMATED_MENU_HEIGHT = 190;
    if (spaceBelow < ESTIMATED_MENU_HEIGHT && rect.top > spaceBelow) {
      setCoords({ bottom: window.innerHeight - rect.top + 4, right });
    } else {
      setCoords({ top: rect.bottom + 4, right });
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    computePosition();
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    // The menu is fixed-positioned; close it on any scroll/resize rather than let it detach.
    const onReposition = () => setOpen(false);
    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, computePosition]);

  return (
    <MenuWrapper ref={wrapperRef}>
      <MenuButton
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label="Member actions"
      >
        <MoreHorizontal />
      </MenuButton>
      {open &&
        coords &&
        createPortal(
          <DropdownMenu
            ref={menuRef}
            $open={open}
            style={{ top: coords.top, bottom: coords.bottom, right: coords.right }}
          >
            <DropdownItem
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(member);
                setOpen(false);
              }}
            >
              View Details
            </DropdownItem>
            {canEditMember && (
              <DropdownItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(member);
                  setOpen(false);
                }}
              >
                Edit Member
              </DropdownItem>
            )}
            {canEdit && member.loginActivated === false && (
              <DropdownItem
                onClick={(e) => {
                  e.stopPropagation();
                  onActivateLogin(member);
                  setOpen(false);
                }}
              >
                Activate Login
              </DropdownItem>
            )}
            {/* Reinstate a departed / deactivated member — re-enables their sign-in too. */}
            {canReactivate && !isActive && (
              <DropdownItem
                onClick={(e) => {
                  e.stopPropagation();
                  onReactivate(member);
                  setOpen(false);
                }}
              >
                Reactivate
              </DropdownItem>
            )}
            {/* Departure only makes sense for a member who hasn't already departed. */}
            {canDepart && isActive && (
              <DropdownItem
                $danger
                onClick={(e) => {
                  e.stopPropagation();
                  onDepart(member);
                  setOpen(false);
                }}
              >
                Departure Wizard
              </DropdownItem>
            )}
          </DropdownMenu>,
          document.body,
        )}
    </MenuWrapper>
  );
};

const PAGE_SIZE = 25;

export const MembersTab = ({ onMemberClick }: MembersTabProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [loginFilter, setLoginFilter] = useState('');
  const [clientPage, setClientPage] = useState(1);
  const [editMember, setEditMember] = useState<Member | null>(null);
  const [departureMember, setDepartureMember] = useState<Member | null>(null);
  const [reactivatingMember, setReactivatingMember] = useState<Member | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showAssignLocation, setShowAssignLocation] = useState(false);
  const { hasPermission } = useAuthStore();
  const canEdit = hasPermission('team:manage');
  const canManageMember = canEdit;
  // Add/edit member, location assignment, and the departure wizard are gated by the
  // org-admin-only team:manage:actions permission; activate-login stays on team:manage.
  const canManageActions = hasPermission('team:manage:actions');
  const canDepart = canManageActions;
  // Same permission as POST /members/:id/reactivate (team:manage:actions).
  const canReactivate = canManageActions;
  const queryClient = useQueryClient();

  const { warning: successMsg, showWarning: showSuccess, clear: clearSuccess } = useAutoDismissWarning('Done', 5000);
  const { warning: errorMsg, showWarning: showError, clear: clearError } = useAutoDismissWarning('Failed to activate login. Please try again.');

  // Single "Activate Login" — surface the generated temp password once, via TempPasswordModal.
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [tempPasswordLabel, setTempPasswordLabel] = useState('');

  // Bulk "Activate Login" — offer a credentials CSV download for the newly activated members.
  const [bulkCredentials, setBulkCredentials] = useState<ActivateLoginCredential[] | null>(null);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['members', search, statusFilter],
    queryFn: () =>
      userService.getAllMembers({
        search: search || undefined,
        status: (statusFilter as 'active' | 'inactive' | undefined) || undefined,
      }),
  });

  const activateLoginMutation = useMutation({
    mutationFn: (member: Member) => memberService.activateLogin(member.id),
    onSuccess: (result, member) => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      if (result.temporaryPassword) {
        setTempPassword(result.temporaryPassword);
        setTempPasswordLabel(`${member.firstName} ${member.lastName} (${member.email})`);
        if (result.entraProvisioned === false) {
          // Password was generated but Entra provisioning failed — it won't work to
          // sign in yet. Surface this alongside the password so it isn't shared as-is.
          showError(
            'Login provisioning is pending — this password will not work until it is retried via Reset Password.',
          );
        }
      } else {
        showSuccess(`Login activated. ${member.firstName} will receive a welcome email.`);
      }
    },
    onError: () => showError(undefined),
  });

  const bulkActivateLoginMutation = useMutation({
    mutationFn: (memberIds: string[]) => memberService.bulkActivateLogin(memberIds),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      setSelected(new Set());
      const failedNote = result.entraFailed > 0 ? ` ${result.entraFailed} did not fully provision — retry via Reset Password.` : '';
      showSuccess(`Login activated for ${result.activated} member${result.activated === 1 ? '' : 's'}.${failedNote}`);
      if (result.credentials.length > 0) {
        setBulkCredentials(result.credentials);
      }
    },
    onError: () => showError(undefined),
  });

  const handleTempPasswordClose = () => {
    setTempPassword(null);
    setTempPasswordLabel('');
  };

  const downloadBulkCredentials = () => {
    if (!bulkCredentials) return;
    downloadCredentialsCsv(bulkCredentials, 'login-activation-credentials.csv');
  };

  // Derive unique roles for filter dropdown from the full fetched set
  const allRoles = useMemo(
    () => Array.from(new Map(members.flatMap((m) => m.roles ?? []).map((r) => [r.id, r])).values()),
    [members],
  );

  // Client-side filters applied to the full fetched set
  const filtered = useMemo(
    () =>
      members.filter((m) => {
        if (roleFilter && !m.roles?.some((r) => r.id === roleFilter)) return false;
        if (loginFilter === 'activated' && !m.loginActivated) return false;
        if (loginFilter === 'not-activated' && m.loginActivated) return false;
        return true;
      }),
    [members, roleFilter, loginFilter],
  );

  // Reset to page 1 whenever any filter or search changes
  useEffect(() => {
    setClientPage(1);
  }, [search, statusFilter, roleFilter, loginFilter]);

  // Client-side pagination over filtered set
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(clientPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pagedFiltered = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  const rangeStart = filtered.length === 0 ? 0 : pageStart + 1;
  const rangeEnd = Math.min(pageStart + PAGE_SIZE, filtered.length);

  const toggleMember = useCallback((memberId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  }, []);

  // Select-all operates over the full filtered set (cross-page)
  const { allFilteredSelected, someFilteredSelected, hasUnactivatedSelected } = useMemo(
    () => ({
      allFilteredSelected: filtered.length > 0 && filtered.every((m) => selected.has(m.id)),
      someFilteredSelected: filtered.some((m) => selected.has(m.id)),
      hasUnactivatedSelected: filtered.some((m) => selected.has(m.id) && !m.loginActivated),
    }),
    [filtered, selected],
  );

  const toggleSelectAll = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((m) => next.delete(m.id));
      } else {
        filtered.forEach((m) => next.add(m.id));
      }
      return next;
    });
  }, [allFilteredSelected, filtered]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  return (
    <>
      <WarningToast message={successMsg} onDismiss={clearSuccess} variant="success" />
      <WarningToast message={errorMsg} onDismiss={clearError} variant="error" />

      {bulkCredentials && (
        <CredentialsBar>
          <CredentialsText>{t('bulkUserUpload.credentialsNote')}</CredentialsText>
          <SelectionActions>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Download size={14} />}
              onClick={downloadBulkCredentials}
            >
              {t('bulkUserUpload.downloadCredentials')}
            </Button>
            <Button variant="ghost" size="sm" leftIcon={<X />} onClick={() => setBulkCredentials(null)}>
              {t('bulkUserUpload.doneButton')}
            </Button>
          </SelectionActions>
        </CredentialsBar>
      )}

      <FiltersRow>
        <SearchWrapper>
          <Input
            type="search"
            placeholder="Search name or email..."
            leftIcon={<Search />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
          />
        </SearchWrapper>
        <FilterSelect value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          {allRoles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </FilterSelect>
        <FilterSelect value={loginFilter} onChange={(e) => setLoginFilter(e.target.value)}>
          <option value="">All Login Access</option>
          <option value="activated">Activated</option>
          <option value="not-activated">Not Activated</option>
        </FilterSelect>
      </FiltersRow>

      {canEdit && selected.size > 0 && (
        <SelectionBar>
          <SelectionCount>
            {selected.size} member{selected.size === 1 ? '' : 's'} selected
          </SelectionCount>
          <SelectionActions>
            {hasUnactivatedSelected && (
              <Button
                variant="primary"
                size="sm"
                leftIcon={<KeyRound size={14} />}
                isLoading={bulkActivateLoginMutation.isPending}
                disabled={bulkActivateLoginMutation.isPending}
                onClick={() => bulkActivateLoginMutation.mutate(selectedIds)}
              >
                Activate Login
              </Button>
            )}
            {canManageActions && (
              <Button
                variant="primary"
                size="sm"
                leftIcon={<MapPin />}
                onClick={() => setShowAssignLocation(true)}
              >
                Assign Location
              </Button>
            )}
            <Button variant="ghost" size="sm" leftIcon={<X />} onClick={clearSelection}>
              Clear
            </Button>
          </SelectionActions>
        </SelectionBar>
      )}

      <TableWrapper>
        <ResponsiveTableWrapper>
          <Table>
            <TableHead>
              <tr>
                {canEdit && (
                  <CheckHeader>
                    <Checkbox
                      type="checkbox"
                      aria-label="Select all members"
                      checked={allFilteredSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected;
                      }}
                      onChange={toggleSelectAll}
                    />
                  </CheckHeader>
                )}
                <TableHeader>Name</TableHeader>
                <TableHeader>Email</TableHeader>
                <TableHeader>Role</TableHeader>
                <TableHeader>Groups</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Login</TableHeader>
                <TableHeader />
              </tr>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <tr>
                  <td colSpan={canEdit ? 8 : 7}>
                    <LoadingState>Loading members...</LoadingState>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 8 : 7}>
                    <EmptyState>No members found</EmptyState>
                  </td>
                </tr>
              ) : (
                pagedFiltered.map((member) => (
                  <TableRow key={member.id} onClick={() => onMemberClick(member)}>
                    {canEdit && (
                      <CheckCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          type="checkbox"
                          aria-label={`Select ${member.firstName} ${member.lastName}`}
                          checked={selected.has(member.id)}
                          onChange={() => toggleMember(member.id)}
                        />
                      </CheckCell>
                    )}
                    <NameCell>
                      {member.firstName} {member.lastName}
                    </NameCell>
                    <EmailCell>{member.email}</EmailCell>
                    <TableCell>
                      {member.roles && member.roles.length > 0 ? (
                        <Badge roleName={member.roles[0].name}>{member.roles[0].name}</Badge>
                      ) : (
                        <NoneText>—</NoneText>
                      )}
                    </TableCell>
                    <GroupsCell>
                      <GroupChips>
                        {member.groups && member.groups.length > 0 ? (
                          member.groups.map((g, idx) => (
                            <GroupChip key={g.id} $index={idx}>
                              {g.name}
                            </GroupChip>
                          ))
                        ) : (
                          <NoneText>—</NoneText>
                        )}
                      </GroupChips>
                    </GroupsCell>
                    <TableCell>
                      <StatusText $active={member.status === 'active'}>
                        {member.status === 'active' ? 'Active' : 'Inactive'}
                      </StatusText>
                    </TableCell>
                    <TableCell>
                      {member.loginActivated === true ? (
                        <Badge variant="success">Activated</Badge>
                      ) : (
                        <Badge variant="neutral">Not Activated</Badge>
                      )}
                    </TableCell>
                    <ActionsCell>
                      <RowMenuCell
                        member={member}
                        onViewDetails={onMemberClick}
                        onEdit={(m) => setEditMember(m)}
                        onDepart={(m) => setDepartureMember(m)}
                        onReactivate={(m) => setReactivatingMember(m)}
                        onActivateLogin={activateLoginMutation.mutate}
                        canEdit={canManageMember}
                        canEditMember={canManageActions}
                        canDepart={canDepart}
                        canReactivate={canReactivate}
                      />
                    </ActionsCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ResponsiveTableWrapper>
        <Footer>
          {/* Seats-used statement removed. Empty span keeps pagination right-aligned
              (Footer uses justify-content: space-between). */}
          <span />
          {totalPages > 1 && (
            <PaginationControls>
              <PaginationInfo>
                Showing {rangeStart}–{rangeEnd} of {filtered.length} member{filtered.length !== 1 ? 's' : ''}
              </PaginationInfo>
              <PageButton
                aria-label="Previous page"
                disabled={safePage <= 1}
                onClick={() => setClientPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft />
              </PageButton>
              <PageButton
                aria-label="Next page"
                disabled={safePage >= totalPages}
                onClick={() => setClientPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight />
              </PageButton>
            </PaginationControls>
          )}
        </Footer>
      </TableWrapper>

      {/* Edit Member Modal */}
      {editMember && (
        <EditMemberModal
          member={editMember}
          isOpen={!!editMember}
          onClose={() => setEditMember(null)}
        />
      )}

      {/* Departure Wizard Modal */}
      {departureMember && (
        <DepartureWizardModal
          member={departureMember}
          isOpen={!!departureMember}
          onClose={() => setDepartureMember(null)}
        />
      )}

      {/* Reactivate (reinstate) Modal */}
      {reactivatingMember && (
        <ReactivateMemberModal
          member={reactivatingMember}
          isOpen={!!reactivatingMember}
          onClose={() => setReactivatingMember(null)}
          onReactivated={(m, result) =>
            showSuccess(
              result.signInEnabled
                ? `${m.firstName} ${m.lastName} has been reactivated and their sign-in re-enabled.`
                : `${m.firstName} ${m.lastName} has been reactivated.`,
            )
          }
        />
      )}

      {/* Bulk Assign Location Panel */}
      {showAssignLocation && selectedIds.length > 0 && (
        <BulkAssignLocationPanel
          memberIds={selectedIds}
          onClose={() => setShowAssignLocation(false)}
          onDone={() => {
            setShowAssignLocation(false);
            clearSelection();
          }}
        />
      )}

      <TempPasswordModal
        isOpen={tempPassword !== null}
        onClose={handleTempPasswordClose}
        temporaryPassword={tempPassword}
        userLabel={tempPasswordLabel}
      />
    </>
  );
};

export default MembersTab;
